import { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Plus, Trash2, XCircle, Clock, Globe, Check } from 'lucide-react'
import { AdsCustomModal } from '@components/wrapper-components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { useCenterStore, useOrganisationStore, useUserStore } from '@caradasstore'
import { getScheduleAvailability, updateScheduleAvailability } from '../../../api/api'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { Logger } from '../../../logger'
import { WeekdaysEnum } from 'enums/enums'
import { cn } from 'lib/utils'
import Dayjs from 'dayjs'

const buildTimeOptions = (step = 15) => {
  const opts = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const ampm = h < 12 ? 'AM' : 'PM'
      const display = `${String(h % 12 || 12).padStart(2, '0')}:${mm} ${ampm}`
      opts.push({ value: `${hh}:${mm}`, display })
    }
  }
  return opts
}

const SlotTimePicker = ({ value, onChange, error, id, type }) => {
  const opts = useMemo(() => buildTimeOptions(15), [])
  const current = opts.find((o) => o.value === value)
  return (
    <Select value={value || ''} onValueChange={(v) => onChange(id, type, v)}>
      <SelectTrigger
        className={cn(
          'h-10 w-[160px] justify-between rounded-lg border-[#D1D5D8] bg-white px-3 text-[13px] font-medium text-[#0A1628]',
          '[&>span]:line-clamp-none [&>span]:whitespace-nowrap',
          '[&>svg.lucide-chevron-down]:hidden',
          error && 'border-error'
        )}
      >
        <SelectValue placeholder='—'>{current?.display}</SelectValue>
        <Clock className='ml-2 h-4 w-4 shrink-0 text-[#475569]' />
      </SelectTrigger>
      <SelectContent className='max-h-[260px]'>
        {opts.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.display}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

SlotTimePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.oneOf(['start', 'end']).isRequired
}

const ScheduleAvailability = ({ hasUnsavedChanges, setHasUnsavedChanges, onRegisterSave }) => {
  const [availabilities, setAvailabilities] = useState([])
  const [showUpdatedPopup, setShowUpdatedPopup] = useState(false)
  const currentUser = useUserStore((store) => store.currentUser)
  const ccId = useCenterStore((store) => store.ccId)
  const organisationId = useOrganisationStore((store) => store.organisationId)
  const organisationDetail = useOrganisationStore((store) => store.organisationDetail)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const initialAvailabilitiesRef = useRef([])
  const [timeErrors, setTimeErrors] = useState({})

  const timezoneDisplay = organisationDetail?.timezone_at_name || ''

  const handleAddTimeSlot = (id) => {
    setAvailabilities((prev) => prev.map((a) => (a.id === id ? { ...a, start: '08:00', end: '17:00', isDayUnAvailable: false } : a)))
  }

  const handleRemoveTimeSlot = (id) => {
    setAvailabilities((prev) => prev.map((a) => (a.id === id ? { ...a, start: '00:00', end: '00:00', isDayUnAvailable: true } : a)))
  }

  const handleTimeChange = (id, type, value) => {
    setAvailabilities((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a
        const start = type === 'start' ? value : a.start
        const end = type === 'end' ? value : a.end
        const newErrors = { ...timeErrors }
        if (Dayjs(start, 'HH:mm').isBefore(Dayjs(end, 'HH:mm'))) {
          delete newErrors[id]
          setTimeErrors(newErrors)
          return { ...a, [type]: value }
        }
        newErrors[id] = 'Start time must be less than the end time.'
        setTimeErrors(newErrors)
        return a
      })
    )
  }

  const formatTime = (timeArray) => `${timeArray[0].toString().padStart(2, '0')}:${timeArray[1].toString().padStart(2, '0')}`

  const transformAvailability = (fetched) =>
    fetched
      .map((a) => {
        const start = formatTime(a.start_time)
        const end = formatTime(a.end_time)
        return { start, end, isDayUnAvailable: start === '00:00' && end === '00:00', ...a }
      })
      .sort((a, b) => WeekdaysEnum[a.day] - WeekdaysEnum[b.day])

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setLoading(true)
        const response = await getScheduleAvailability({ ccId, organisation_id: organisationId })
        const fetched = response?.data
        if (fetched && fetched.length > 0) {
          const transformed = transformAvailability(fetched)
          setAvailabilities(transformed)
          initialAvailabilitiesRef.current = transformed
        }
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'scheduleAvailability', method: 'fetchAvailability' } })
      } finally {
        setLoading(false)
      }
    }
    if (ccId && organisationId) fetchAvailability()
  }, [ccId, organisationId])

  const prepareAvailabilityData = () =>
    availabilities.map((a) => ({
      id: a.id,
      organisation_id: organisationId,
      day: a.day?.toUpperCase(),
      start_time: a.start || '00:00',
      end_time: a.end || '00:00',
      updated_by: currentUser.id
    }))

  const handleSubmit = async () => {
    try {
      setLoading(true)
      const organisationsAvailability = prepareAvailabilityData()
      const data = { ccId, organisation_id: organisationId, organisationsAvailability }
      const response = await updateScheduleAvailability(data)
      const updated = response?.data
      setShowUpdatedPopup(true)
      if (updated && updated.length > 0) {
        const transformed = transformAvailability(updated)
        setAvailabilities(transformed)
        initialAvailabilitiesRef.current = transformed
        setHasUnsavedChanges(false)
      }
      setTimeout(() => setShowUpdatedPopup(false), 3000)
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'scheduleAvailability', method: 'handleSubmit' } })
    } finally {
      setLoading(false)
    }
  }

  const handleCopyMondayToWeekdays = () => {
    const monday = availabilities.find((a) => a.day?.toUpperCase() === 'MONDAY')
    if (!monday) return
    const weekdayKeys = ['TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']
    setAvailabilities((prev) =>
      prev.map((a) =>
        weekdayKeys.includes(a.day?.toUpperCase())
          ? { ...a, start: monday.start, end: monday.end, isDayUnAvailable: monday.isDayUnAvailable }
          : a
      )
    )
  }

  const handleResetDefaults = () => {
    setAvailabilities((prev) =>
      prev.map((a) => {
        const upper = a.day?.toUpperCase()
        if (upper === 'SATURDAY' || upper === 'SUNDAY') {
          return { ...a, start: '00:00', end: '00:00', isDayUnAvailable: true }
        }
        return { ...a, start: '08:00', end: '17:00', isDayUnAvailable: false }
      })
    )
  }

  useEffect(() => {
    const isModified = JSON.stringify(initialAvailabilitiesRef.current) !== JSON.stringify(availabilities)
    setHasUnsavedChanges(isModified)
  }, [availabilities, setHasUnsavedChanges])

  useEffect(() => {
    if (!onRegisterSave) return
    const disabled = !availabilities || availabilities.length === 0 || !hasUnsavedChanges || Object.keys(timeErrors).length > 0
    onRegisterSave({ onSave: handleSubmit, disabled })
  }, [availabilities, hasUnsavedChanges, timeErrors, onRegisterSave])

  return (
    <div className='space-y-5'>
      <div className='flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] px-4 py-3'>
        <div className='flex items-start gap-3'>
          <span className='inline-flex h-9 w-9 flex-none items-center justify-center rounded-md bg-white'>
            <Clock className='h-4 w-4 text-[#0A1628]' />
          </span>
          <div>
            <div className='text-[14px] font-bold text-[#0A1628]'>Weekly availability</div>
            <div className='mt-0.5 text-[13px] text-[#475569]'>
              Set the time slots when bookings are accepted. This pattern repeats every week.
            </div>
          </div>
        </div>
        <div className='inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-[12px]'>
          <Globe className='h-3.5 w-3.5 text-[#475569]' />
          <span className='font-semibold uppercase tracking-wide text-[#6B7280]'>Store Time Zone</span>
          <span className='font-semibold text-[#0A1628]'>{timezoneDisplay || '—'}</span>
        </div>
      </div>

      <div className='rounded-xl border border-[#EBEDF0] bg-white'>
        {availabilities.map((a, idx) => (
          <div
            key={a.id}
            className={`flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:gap-4 ${
              idx === availabilities.length - 1 ? '' : 'border-b border-[#EBEDF0]'
            }`}
          >
            <div className='w-24 text-[12px] font-bold uppercase tracking-wide text-[#0A1628] sm:w-28'>{a.day?.toUpperCase()}</div>
            {a.isDayUnAvailable ? (
              <div className='flex flex-1 items-center justify-between gap-3'>
                <span className='inline-flex items-center gap-2 rounded-md bg-[#F1F4F8] px-2.5 py-1 text-[12px] text-[#6B7280]'>
                  <span className='h-1.5 w-1.5 rounded-full bg-[#9CA3AF]' />
                  Unavailable
                </span>
                <button
                  type='button'
                  onClick={() => handleAddTimeSlot(a.id)}
                  aria-label={`Add hours for ${a.day}`}
                  className='inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#D1D5D8] bg-white text-[#0A1628] hover:bg-[#F8FAFC]'
                >
                  <Plus className='h-4 w-4' />
                </button>
              </div>
            ) : (
              <div className='flex flex-1 flex-wrap items-center gap-3'>
                <SlotTimePicker value={a.start} type='start' id={a.id} onChange={handleTimeChange} error={!!timeErrors[a.id]} />
                <span className='text-[13px] text-[#6B7280]'>to</span>
                <SlotTimePicker value={a.end} type='end' id={a.id} onChange={handleTimeChange} error={!!timeErrors[a.id]} />
                <button
                  type='button'
                  onClick={() => handleRemoveTimeSlot(a.id)}
                  aria-label={`Clear hours for ${a.day}`}
                  className='inline-flex h-8 w-8 items-center justify-center rounded-md text-[#475569] hover:text-[#0A1628]'
                >
                  <XCircle className='h-5 w-5' />
                </button>
                <button
                  type='button'
                  onClick={() => handleRemoveTimeSlot(a.id)}
                  aria-label={`Remove ${a.day}`}
                  className='ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-[#475569] hover:bg-[#F8FAFC]'
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              </div>
            )}
            {timeErrors[a.id] && <div className='text-[11px] text-[#DC2626] sm:basis-full sm:pl-28'>{timeErrors[a.id]}</div>}
          </div>
        ))}
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <button
          type='button'
          onClick={handleCopyMondayToWeekdays}
          className='inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D1D5D8] bg-white text-[13px] font-medium text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
        >
          <Check className='h-4 w-4' />
          Copy Monday to weekdays
        </button>
        <button
          type='button'
          onClick={handleResetDefaults}
          className='inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D1D5D8] bg-white text-[13px] font-medium text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
        >
          <Check className='h-4 w-4' />
          Reset to defaults
        </button>
      </div>

      {/* CAR-4050: the standard pop-up footer instead of its own button; Okay, the X and Escape all close it. */}
      <AdsCustomModal
        title='Availability Updated'
        description='Your Schedule Availability selections have been updated successfully.'
        open={showUpdatedPopup}
        onClose={() => setShowUpdatedPopup(false)}
        successButtonTitle='Okay'
      />
    </div>
  )
}

ScheduleAvailability.propTypes = {
  hasUnsavedChanges: PropTypes.bool,
  setHasUnsavedChanges: PropTypes.func,
  onRegisterSave: PropTypes.func
}

export default ScheduleAvailability
